import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, euro, formatDate } from "@/lib/api";
import { ArrowLeft, Envelope, Phone, PencilSimple, FloppyDisk, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import NewBookingButton from "@/components/NewBookingButton";

const FIELDS = [
  ["first_name", "Vorname"],
  ["last_name", "Nachname"],
  ["company", "Firma"],
  ["email", "E-Mail"],
  ["phone", "Telefon"],
  ["language", "Sprache"],
  ["street", "Straße & Hausnr."],
  ["postal_code", "PLZ"],
  ["city", "Ort"],
  ["country", "Land"],
];

const BILLING_FIELDS = [
  ["billing_company", "Rechnungs-Firma"],
  ["billing_attention", "z.Hd. (Ansprechpartner)"],
  ["billing_name", "Name auf Rechnung"],
  ["billing_address", "Rechnungs-Adresse (Straße, PLZ Ort, Land)"],
];

export default function GuestDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/guests/${id}`).then(r => setD(r.data));
  useEffect(() => { load(); }, [id]);

  const startEdit = () => {
    const g = d.guest;
    setForm({
      first_name: g.first_name || "",
      last_name: g.last_name || "",
      company: g.company || "",
      email: g.email || "",
      phone: g.phone || "",
      language: g.language || "Deutsch",
      street: g.street || "",
      postal_code: g.postal_code || "",
      city: g.city || "",
      country: g.country || "Deutschland",
      notes: g.notes || "",
      billing_company: g.billing_company || "",
      billing_attention: g.billing_attention || "",
      billing_name: g.billing_name || "",
      billing_address: g.billing_address || "",
      gdpr_consent: g.gdpr_consent || false,
      newsletter_consent: g.newsletter_consent || false,
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/guests/${id}`, form);
      toast.success("Gastdaten gespeichert");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally { setSaving(false); }
  };

  if (!d) return <div className="text-muted-foreground">Lade Gast…</div>;
  const g = d.guest;

  return (
    <div className="space-y-6" data-testid="guest-detail-page">
      <Link to="/gaeste" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16}/> Zurück
      </Link>

      <div className="msb-card">
        {!editing ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>{g.first_name} {g.last_name}</h1>
                {g.company && <div className="text-muted-foreground mt-1">{g.company}</div>}
                <div className="mt-3 flex flex-wrap gap-4 text-sm">
                  {g.email && <span className="inline-flex items-center gap-1.5"><Envelope size={16}/> {g.email}</span>}
                  {g.phone && <span className="inline-flex items-center gap-1.5"><Phone size={16}/> {g.phone}</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <NewBookingButton guest={g} label="Neue Buchung für diesen Gast" testId="guest-new-booking-btn"
                                  className="rounded-xl bg-primary hover:bg-primary/90 text-white gap-1.5"/>
                <Button onClick={startEdit} variant="outline" className="rounded-xl gap-1.5" data-testid="guest-edit-btn">
                  <PencilSimple size={16}/> Bearbeiten
                </Button>
                <div className="text-sm text-right">
                  <div className="text-muted-foreground">Sprache: {g.language}</div>
                  <div className="text-muted-foreground">Erstellt: {formatDate(g.created_at)}</div>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
              {g.street}, {g.postal_code} {g.city}, {g.country}
            </div>
            {g.notes && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">Notizen</div>
                <div className="text-sm whitespace-pre-line text-foreground">{g.notes}</div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4" data-testid="guest-edit-form">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Gastdaten bearbeiten</h2>
              <Button variant="ghost" onClick={()=>setEditing(false)} className="rounded-lg"><X size={18}/></Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {FIELDS.map(([k, label]) => (
                <div key={k}>
                  <Label>{label}</Label>
                  <Input value={form[k] || ""} onChange={e=>setForm({...form, [k]: e.target.value})}
                         className="rounded-xl mt-1.5" data-testid={`guest-edit-${k}`}/>
                </div>
              ))}
            </div>
            <fieldset className="border border-border rounded-xl p-4 space-y-3">
              <legend className="px-2 text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                Rechnungs-Adresse (optional · wird automatisch übernommen)
              </legend>
              <div className="text-[11px] text-muted-foreground -mt-1">
                Nur ausfüllen, wenn die Rechnung an eine andere Firma/Adresse geht als die Gastanschrift.
                Wird bei jeder Rechnung dieses Gastes automatisch als Rechnungsempfänger vorbelegt.
              </div>
              <div className="grid grid-cols-2 gap-3">
                {BILLING_FIELDS.map(([k, label]) => (
                  <div key={k} className={k === "billing_address" ? "col-span-2" : ""}>
                    <Label>{label}</Label>
                    <Input value={form[k] || ""} onChange={e=>setForm({...form, [k]: e.target.value})}
                           className="rounded-xl mt-1.5" data-testid={`guest-edit-${k}`}/>
                  </div>
                ))}
              </div>
            </fieldset>
            <div>
              <Label>Notizen (dauerhaft am Gast)</Label>
              <textarea value={form.notes || ""} onChange={e=>setForm({...form, notes: e.target.value})}
                        rows={3}
                        className="w-full rounded-xl mt-1.5 border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        data-testid="guest-edit-notes"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={()=>setEditing(false)} className="rounded-xl">Abbrechen</Button>
              <Button onClick={save} disabled={saving} className="rounded-xl bg-primary gap-1.5" data-testid="guest-edit-save">
                <FloppyDisk size={16}/> {saving ? "Speichere…" : "Speichern"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="msb-card">
          <h3 className="text-lg font-semibold mb-3" style={{fontFamily:"'Work Sans',sans-serif"}}>Buchungen ({d.bookings.length})</h3>
          {d.bookings.map(b => (
            <div key={b.id} className="py-2 border-b border-border last:border-0">
              <div className="flex justify-between items-center">
                <div><div className="font-medium text-sm">{formatDate(b.check_in)} → {formatDate(b.check_out)}</div>
                     <div className="text-xs text-muted-foreground">{b.nights} Nächte · {b.status}</div></div>
                <div className="text-right"><div className="font-semibold">{euro(b.gross)}</div>
                     <div className="text-xs text-muted-foreground">{b.payment_status}</div></div>
              </div>
            </div>
          ))}
          {d.bookings.length === 0 && <div className="text-muted-foreground text-sm">Noch keine Buchungen.</div>}
        </div>
        <div className="msb-card">
          <h3 className="text-lg font-semibold mb-3" style={{fontFamily:"'Work Sans',sans-serif"}}>Rechnungen ({d.invoices.length})</h3>
          {d.invoices.map(i => (
            <div key={i.id} className="py-2 border-b border-border last:border-0 flex justify-between items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm">{i.number}</div>
                <div className="text-xs text-muted-foreground">{formatDate(i.invoice_date)} · {i.status}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-semibold">{euro(i.gross)}</div>
              </div>
              <a href={`${API}/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer"
                 className="text-xs px-2 py-1 rounded border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 inline-flex items-center gap-1 flex-shrink-0"
                 data-testid={`guest-inv-pdf-${i.id}`}>
                📄 PDF
              </a>
            </div>
          ))}
          {d.invoices.length === 0 && <div className="text-muted-foreground text-sm">Noch keine Rechnungen.</div>}
        </div>
      </div>

      <div className="msb-card">
        <h3 className="text-lg font-semibold mb-3" style={{fontFamily:"'Work Sans',sans-serif"}}>Nachrichtenverlauf</h3>
        {d.messages.map(m => (
          <div key={m.id} className="py-2 border-b border-border last:border-0 flex justify-between text-sm">
            <div><div className="font-medium">{m.subject}</div>
                 <div className="text-xs text-muted-foreground">{m.type} · {m.recipient}</div></div>
            <div className="text-right text-xs text-muted-foreground">{formatDate(m.sent_at)} · {m.status}</div>
          </div>
        ))}
        {d.messages.length === 0 && <div className="text-muted-foreground text-sm">Keine Nachrichten versendet.</div>}
      </div>
    </div>
  );
}
